import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Camera, MapPin, Clock, CheckCircle2, LogOut as LogOutIcon, Calendar, Plus } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

/**
 * Phase 2 — Promoter Attendance (mobile).
 *
 * Captures: geolocation (browser API), photo (front camera with file-input
 * fallback when getUserMedia fails or the device blocks it), optional remarks.
 * Shows today's status (Not checked in / On field / Day complete) and the last
 * 5 leave requests with their status.
 */
export default function PromoterAttendance() {
  const [today, setToday] = useState(null);
  const [busy, setBusy] = useState(false);
  const [photoDataUrl, setPhotoDataUrl] = useState("");
  const [remarks, setRemarks] = useState("");
  const [geo, setGeo] = useState({ lat: null, lng: null, err: null });
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const loadToday = async () => {
    try {
      const { data } = await api.get("/promoter/attendance/today");
      setToday(data && data.id ? data : null);
    } catch (e) {
      toast.error("Could not load today", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  useEffect(() => {
    loadToday();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy, err: null }),
        (err) => setGeo({ lat: null, lng: null, acc: null, err: err.message || "Location unavailable" }),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
      );
    } else {
      setGeo({ lat: null, lng: null, acc: null, err: "Geolocation not supported by this browser" });
    }
  }, []);

  const onFilePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Photo too large (max 4 MB)"); return; }
    const r = new FileReader();
    r.onload = () => setPhotoDataUrl(r.result);
    r.onerror = () => toast.error("Could not read photo");
    r.readAsDataURL(f);
  };

  const checkIn = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/promoter/attendance/check-in", {
        latitude: geo.lat, longitude: geo.lng, accuracy: geo.acc,
        photo_url: photoDataUrl || null, remarks: remarks || null,
      });
      toast.success("Checked in");
      setToday(data);
      setRemarks("");
      setPhotoDataUrl("");
    } catch (e) {
      toast.error("Could not check in", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const checkOut = async () => {
    setBusy(true);
    try {
      await api.post("/promoter/attendance/check-out", {
        latitude: geo.lat, longitude: geo.lng, accuracy: geo.acc, remarks: remarks || null,
      });
      toast.success("Checked out — have a good evening!");
      setRemarks("");
      loadToday();
    } catch (e) {
      toast.error("Could not check out", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const status = !today ? "not_started"
    : today.check_out_at ? "completed"
    : "on_field";

  return (
    <div className="space-y-4" data-testid="promoter-attendance-page">
      <header>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Attendance</h1>
        <p className="text-xs text-slate-500 mt-0.5">Mark your day. Capture a selfie when you reach your counter.</p>
      </header>

      <Card data-testid="attendance-today-card">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Clock size={16} className={
              status === "on_field" ? "text-emerald-600"
              : status === "completed" ? "text-slate-500" : "text-amber-600"} />
            <div className="text-sm font-semibold text-slate-900" data-testid="attendance-status-label">
              {status === "not_started" && "Not checked in yet"}
              {status === "on_field" && "On field"}
              {status === "completed" && "Day complete"}
            </div>
            {today?.check_in_late && (
              <span className="ml-auto text-[10px] uppercase font-bold text-rose-700 bg-rose-100 px-2 py-0.5 rounded" data-testid="late-flag">Late</span>
            )}
            {today?.check_out_early && (
              <span className="ml-1 text-[10px] uppercase font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded" data-testid="early-flag">Early</span>
            )}
          </div>
          {today?.check_in_at && (
            <div className="text-xs text-slate-600">Check-in: {fmtTime(today.check_in_at)}</div>
          )}
          {today?.check_out_at && (
            <div className="text-xs text-slate-600">Check-out: {fmtTime(today.check_out_at)}</div>
          )}
          {geo.err && (
            <div className="text-[11px] text-amber-700 flex items-center gap-1" data-testid="geo-error">
              <MapPin size={12} /> Location: {geo.err}
            </div>
          )}
          {geo.lat && (
            <div className="text-[11px] text-emerald-700 flex items-center gap-1" data-testid="geo-ok">
              <MapPin size={12} /> Location captured ({geo.lat.toFixed(4)}, {geo.lng.toFixed(4)})
            </div>
          )}
        </CardContent>
      </Card>

      {status === "not_started" && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="text-sm font-semibold text-slate-900">Check in</div>
            <div className="space-y-2">
              <Label className="text-[11px] uppercase font-semibold text-slate-600">Photo (camera or upload)</Label>
              {photoDataUrl ? (
                <div className="flex items-start gap-2">
                  <img src={photoDataUrl} alt="preview" className="w-16 h-16 rounded border object-cover" />
                  <button type="button" onClick={() => setPhotoDataUrl("")}
                          className="text-xs text-rose-600 hover:underline"
                          data-testid="photo-remove-btn">Remove</button>
                </div>
              ) : (
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}
                        className="w-full" data-testid="attendance-photo-btn">
                  <Camera size={14} className="mr-1" /> Take photo / upload
                </Button>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" capture="user"
                     onChange={onFilePick} className="hidden" data-testid="attendance-photo-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] uppercase font-semibold text-slate-600">Remarks (optional)</Label>
              <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)}
                        rows={2} placeholder="e.g. counter setup ongoing"
                        data-testid="attendance-remarks" />
            </div>
            <Button onClick={checkIn} disabled={busy}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                    data-testid="check-in-btn">
              {busy ? "Checking in..." : "Check In"}
            </Button>
          </CardContent>
        </Card>
      )}

      {status === "on_field" && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="text-sm font-semibold text-slate-900">Wrapping up?</div>
            <div className="space-y-1">
              <Label className="text-[11px] uppercase font-semibold text-slate-600">Remarks (optional)</Label>
              <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2}
                        placeholder="Closing notes for the day" data-testid="attendance-remarks-out" />
            </div>
            <Button onClick={checkOut} disabled={busy}
                    className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
                    data-testid="check-out-btn">
              <LogOutIcon size={14} className="mr-1" /> {busy ? "Checking out..." : "Check Out"}
            </Button>
          </CardContent>
        </Card>
      )}

      {status === "completed" && (
        <Card>
          <CardContent className="p-4 text-center space-y-2" data-testid="attendance-complete-card">
            <CheckCircle2 size={28} className="mx-auto text-emerald-600" />
            <div className="font-semibold text-slate-900 text-sm">Day complete</div>
            <div className="text-xs text-slate-500">See you tomorrow.</div>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-2">
        <Button variant="outline" onClick={() => navigate("/promoter/leave")} className="flex-1"
                data-testid="leave-request-link">
          <Calendar size={14} className="mr-1" /> Request leave
        </Button>
        <Button variant="outline" onClick={() => navigate("/promoter/my-attendance")} className="flex-1"
                data-testid="my-attendance-link">
          <Plus size={14} className="mr-1" /> My month
        </Button>
      </div>
    </div>
  );
}

function fmtTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
